'use strict';

/**
 * Module for calculations. All functions return strings to avoid floating point errors. If you need a number, consider using "round"
 * function to convert the result to a number with a specified number of decimal places (default is two) or toNumber function. However,
 * those will throw an error if the conversion would lose precision. If you use "round" or "toNumber" functions, that way you can probably
 * spot potential issues with precision loss and handle them accordingly.
 *
 * All input values can be numbers, strings or Big instances and they will be converted to Big instances internally (passed as strings).
 * Functions return strings except for toNumber and round functions.
 *
 * @example
 * const calc from '@/helpers/calc'
 *
 * // Input value can be a number, string or Big instance. Numbers are converted to strings internally first.
 * const summed1 = calc.sum(1, 2, 3, 4, 5); // "15"
 * const summed2 = calc.sum([1, 2, 3, 4, 5], 2); // "17"
 * const averaged = calc.average(1, 2, 3, 4, 5); // "3"
 * const added = calc.add(1, 2); // "3"
 * const subtracted = calc.subtract("5", "3"); // "2"
 * const multiplied = calc.multiply(2, 3); // "6"
 * const divided = calc.divide(10, 2); // "5"
 * const amount = calc.percentOf(120, 25.5); // "30.6"
 * const addedPercent = calc.addPercentage(120, 25.5); // "150.6"
 * const subtractedPercentage = calc.subtractPercentage(120, 25.5); // "89.4"
 * const percentChange = calc.percentChange(6, 4); // "-33.33"
 * const percentDiff = calc.percentDiff(6, 4); // "40"
 * const percentage = calc.calculatePercentage(6, 10); // "60"
 * const rounded = calc.round(1.2345); // 1.23
 * const bigNumber = calc.toBig("1.2345").times("0.35") // Big instance
 * const toFixed = calc.toFixed(1.2345, 2); // "1.23"
 * const toNumber = calc.toNumber(calc.toBig("1.2345")); // 1.2345
 * const toNumber2 = calc.toNumber("123567892345.1233555"), true); // 123567892345.1234
 *
 * // This would give an error (would trigger valueOf), because strict mode is enabled.
 * const bign = +calc.toBig("23");
 *
 * // Instead, use toNumber function to convert a Big instance to a number.
 * const number1 = calc.toNumber(calc.toBig("23")); // 23
 * // Or (this will throw and error if conversion would lose precision):
 * const number2 = calc.toBig("23").toNumber(); // 23
 * // Or (by default rounds to two decimal places and returns a number):
 * const number3 = calc.round(calc.toBig("23")); // 23
 * // This wouldn't throw an error, because average function returns a string. If
 * // you need to convert it to a number, consider using toNumber or round function unles
 * // you are sure that there's not going to be a loss of precision (which is pretty unlikely, unless
 * // divisions are being used).
 * const avg = +calc.average(1, 2, 3, 4, 5); // 3
 */

import Big from 'big.js';

// Set the number of decimal places to 30 (for divisions only). Matches the decimal places used in the database.
Big.DP = 30;
// When set to true, an error will be thrown if a primitive number is passed to the Big constructor, or if valueOf is called, or if toNumber is called on a Big which cannot be converted to a primitive number without a loss of precision. Values passed to the Big constructor must be strings.
Big.strict = true;

export type NumberLike = number | string | Big;

/**
 * Converts a value to a Big instance.
 *
 * @returns {Big} The value as a Big instance.
 */
export function toBig(value: NumberLike): Big {
  if (value instanceof Big) {
    return value;
  }

  return new Big(typeof value === 'string' ? value : String(value));
}

/**
 * Get the raw JS value of a Big instance. Tries to round properly as long as there are decimals.
 *
 * @private
 * @param {Big} value - The value to convert to a number.
 * @returns {number} The value as a number.
 */
function getRawJSValue(value: Big): number {
  const val = value.toFixed();
  const [, jsValDecimals] = String(+val).split('.');
  const decimals = jsValDecimals?.length ?? 0;
  if (decimals && decimals < 17) {
    // 123567892345.1233555 -> 123567892345.12336
    // +"123567892345.1233555" -> 123567892345.12335
    // Also +"123567892345.12336" -> 123567892345.12337 i.e. loss of precision in both cases.
    // In case that happens, this should round properly to 123567892345.1234.
    // Alternatively we could just accept the loss of precision and return the value as is...
    try {
      return value.round(decimals).toNumber();
    } catch {
      return value.round(decimals - 1).toNumber();
    }
  }
  return +val;
}

/**
 * Converts a value to a number.
 *
 * @param {(number|string|Big)} value - The value to convert to a number.
 * @param {boolean} [unsafeConversion=false] - If true, the value will be converted to a number (tries to at least round properly in case there are decimals). Defaults to false.
 * @returns {number} The value as a number.
 */
export function toNumber(value: NumberLike, unsafeConversion = false): number {
  if (value instanceof Big) {
    return unsafeConversion ? getRawJSValue(value) : value.toNumber();
  }

  const val = toBig(typeof value === 'string' ? value : String(value));
  return unsafeConversion ? getRawJSValue(val) : val.toNumber();
}

/**
 * Converts a value to a fixed-point notation. I.e. (when decimals is 2) 1.2345 -> "1.23" or 1.2 --> "1.20".
 *
 * @param {(number|string|Big)} value - The value to convert to a fixed-point notation.
 * @param {(number|false)} [decimals=false] - The number of decimal places to round to. Defaults to the number of decimal places in the value (false).
 * @returns {string} The value in fixed-point notation as a string.
 */
export function toFixed(value: NumberLike, decimals?: number): string {
  return typeof decimals === 'number' && decimals > -1
    ? toBig(value).toFixed(decimals)
    : toBig(value).toFixed();
}

/**
 * Calculates the average of an array of numbers.
 *
 * @returns {string} The average of the numbers as a string.
 */
export function average(...values: (NumberLike | NumberLike[])[]): string {
  const flatValues = values.flat(Infinity) as NumberLike[];
  return new Big(flatValues.reduce<Big>((a, b) => a.plus(toBig(b)), new Big('0')))
    .div(String(flatValues.length))
    .toFixed();
}

/**
 * Divides a value by another value.
 *
 * @returns {string} The result of the division as a string.
 */
export function divide(
  /**
   * The value to be divided.
   */
  value: NumberLike,
  /**
   * The value to divide by.
   */
  by: NumberLike
): string {
  return toBig(value).div(toBig(by)).toFixed();
}

/**
 * Multiplies two numbers.
 *
 * @returns {string} The result of the multiplication as a string.
 */
export function multiply(
  /**
   * The first number to multiply.
   */
  value: NumberLike,
  /**
   * The second number to multiply.
   */
  by: NumberLike
): string {
  return toBig(value).times(toBig(by)).toFixed();
}

/**
 * Subtracts one number from another.
 *
 * @returns {string} The result of the subtraction as a string.
 */
export function subtract(
  /**
   * The number to subtract from.
   */
  value: NumberLike,
  /**
   * The number to subtract.
   */
  by: NumberLike
): string {
  return toBig(value).minus(toBig(by)).toFixed();
}

/**
 * Adds two numbers.
 *
 * @returns {string} The sum of the two numbers as a string.
 */
export function add(
  /**
   * The number to add to.
   */
  value: NumberLike,
  /**
   * The number to add.
   */
  by: NumberLike
): string {
  return toBig(value).plus(toBig(by)).toFixed();
}

/**
 * Calculates the sum of an array of numbers.
 *
 * @returns {string} The sum of the numbers as a string.
 */
export function sum(...values: (NumberLike | NumberLike[])[]): string {
  const flatValues = values.flat(Infinity) as NumberLike[];
  return new Big(flatValues.reduce<Big>((a, b) => a.plus(toBig(b)), new Big('0'))).toFixed();
}

/**
 * Calculates the value of a percentage of a number.
 *
 * @returns {string} The calculated value of the percentage as a string.
 */
export function percentOf(
  /**
   * The value to calculate the value of a percentage of.
   */
  value: NumberLike,
  /**
   * The percentage to calculate.
   */
  percent: NumberLike
): string {
  return toBig(value).times(toBig(percent).div('100')).toFixed();
}

/**
 * Adds a percentage to a value.
 *
 * @returns {string} The new value after adding the percentage, as a string.
 */
export function addPercentage(
  /**
   * The original value.
   */
  value: NumberLike,
  /**
   * The percentage to add to the value.
   */
  percent: NumberLike
): string {
  return toBig(value)
    .plus(toBig(value).times(toBig(percent).div('100')))
    .toFixed();
}

/**
 * Subtracts a percentage from a value. I.e. value = 100, percent = 30, result = 70.
 *
 * @returns {string} The new value after subtracting the percentage, as a string.
 */
export function subtractPercentage(
  /**
   * The original value.
   */
  value: NumberLike,
  /**
   * The percentage to subtract from the value.
   */
  percent: NumberLike
): string {
  return toBig(value)
    .minus(toBig(value).times(toBig(percent).abs().div('100')))
    .toFixed();
}

/**
 * Calculates the percentage change between two values.
 *
 * @returns {string} The percentage change between the two values.
 */
export function percentChange(
  /**
   * The first value.
   */
  value1: NumberLike,
  /**
   * The second value.
   */
  value2: NumberLike,
  /**
   * The number of decimal places to round the result to. Defaults to 2.
   */
  decimals: number | false = 2
): string {
  const change = toBig(value2).minus(toBig(value1)).div(toBig(value1).abs()).times('100');
  return typeof decimals === 'number' && decimals > -1
    ? change.round(decimals).toFixed()
    : change.toFixed();
}

/**
 * Calculates the percentage difference between two positive values.
 *
 * @returns {string} The percentage difference between the two values.
 */
export function percentDiff(
  /**
   * The first value.
   */
  value1: NumberLike,
  /**
   * The second value.
   */
  value2: NumberLike,
  /**
   * The number of decimal places to round the result to. Defaults to 2.
   */
  decimals: number | false = 2
): string {
  if (!toBig(value1).gt('0') || !toBig(value2).gt('0')) {
    throw new Error('Both values must be greater than 0.');
  }

  const diff = toBig(value1)
    .minus(toBig(value2))
    .abs()
    .div(toBig(value1).plus(toBig(value2)).div('2'))
    .times('100');
  return typeof decimals === 'number' && decimals > -1
    ? diff.round(decimals).toFixed()
    : diff.toFixed();
}

/**
 * Calculates the percentage of a part of a total.
 *
 * @returns {string} The percentage of the part of the total.
 */
export function calculatePercentage(
  /**
   * The part of the total.
   */
  part: NumberLike,
  /**
   * The total.
   */
  total: NumberLike,
  decimals = 2
): string {
  const val = toBig(part).div(toBig(total)).times('100');
  return typeof decimals === 'number' && decimals > -1
    ? val.round(decimals).toFixed()
    : val.toFixed();
}

/**
 * Rounds a value to a specified number of decimal places. Since this is the preferred way to convert a Big instance to a number, the default is to return the result as a number. Big.js in strict mode will throw error if value would have >16 digits and toNumber is set to true because it would lose precision.
 *
 * @returns {(number|string)} The rounded value
 */
export function round(value: NumberLike, decimals: number | false): number;
export function round(value: NumberLike, decimals: number | false, asNumber: false): string;
export function round(
  /**
   * The value to round.
   */
  value: NumberLike,
  /**
   * The number of decimal places to round to. Defaults to 2.
   */
  decimals: number | false = 2,
  /**
   * Whether to return the result as a number. Defaults to true.
   */
  asNumber = true
): number | string {
  const rounded =
    typeof decimals === 'number' && decimals > -1 ? toBig(value).round(decimals) : toBig(value);
  return asNumber ? rounded.toNumber() : rounded.toFixed();
}

export default Object.freeze({
  average,
  divide,
  multiply,
  subtract,
  add,
  sum,
  percentOf,
  addPercentage,
  subtractPercentage,
  percentChange,
  percentDiff,
  calculatePercentage,
  round,
  toBig,
  toNumber,
  toFixed,
});
